import React, { useEffect, useState } from "react";
import api from "../../utils/axiosInstance";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Link } from 'react-router-dom';

const SalesrepDashboard = () => {
    const [bookings, setBookings] = useState([]);
    const [filteredBookings, setFilteredBookings] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(6);
    const [filterDate, setFilterDate] = useState(new Date());

    useEffect(() => {
        fetchBookings();
    }, []);

    const fetchBookings = async () => {
        try {
            const res = await api.get("/demo-slots/booked");
            const slots = res.data.bookedSlots || [];
            setBookings(slots);
            setFilteredBookings(slots);
        } catch (err) {
            console.error("Failed to fetch bookings:", err);
        }
    };

    // Filter by date
    useEffect(() => {
        if (!filterDate) {
            setFilteredBookings(bookings);
        } else {
            setFilteredBookings(
                bookings.filter(
                    (b) => new Date(b.date).toLocaleDateString("en-US") === new Date(filterDate).toLocaleDateString("en-US")
                )
            );
            setCurrentPage(1); // reset page on filter
        }
    }, [filterDate, bookings]);

    // Pagination logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentBookings = filteredBookings.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-50">
            {/* Top Navbar */}
            <nav className="bg-[#292626] px-6 py-4 shadow-lg fixed w-full top-0 left-0 z-50 flex items-center justify-between">
                <a href="/" className="flex items-center">
                    <img src="../assets/FleetyBlue.png" alt="Fleety Logo" className="h-12" />
                </a>
                <Link
                    to="/"
                    className="bg-primary text-white border-none px-5 py-2 rounded-full font-semibold flex items-center gap-2 hover:bg-primary-dark hover:-translate-y-0.5 transition"
                >
                    <i className="fas fa-sign-out-alt"></i> Logout
                </Link>
            </nav>

            <div className="w-full p-4 sm:p-6 lg:p-20 pt-28">
                <header className="mb-8">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Live Demo Bookings</h1>
                            <p className="text-gray-600">View all booked demo slots</p>
                        </div>
                        <div>
                            <a
                                href="https://demo.fleetycdl.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-800 transition"
                            >
                                Go to Demo App
                            </a>
                        </div>

                        {/* Date Filter */}
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                            <label className="text-gray-700 font-medium">Search Demo by Date:</label>
                            <DatePicker
                                selected={filterDate ? new Date(filterDate) : null}
                                onChange={(date) => setFilterDate(date)}
                                minDate={new Date()}
                                dateFormat="MM/dd/yyyy"
                                placeholderText="MM/DD/YYYY"
                                className="px-3 py-2 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                            />
                            {filterDate && (
                                <button
                                    onClick={() => setFilterDate("")}
                                    className="px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-800 transition"
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>
                </header>

                {/* Booking Cards Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 items-stretch">
                    {currentBookings.length === 0 ? (
                        <div className="col-span-full text-center text-gray-500 text-lg py-12">
                            No bookings found
                        </div>
                    ) : (
                        currentBookings.map((booking) => (
                            booking.email != null && (
                                <div
                                    key={booking._id}
                                    className="relative h-full flex flex-col bg-white shadow-lg hover:shadow-xl hover:-translate-y-1 hover:scale-[1.01] transition rounded-3xl overflow-hidden"
                                >
                                    {/* Header */}
                                    <div className="bg-blue-700 py-6 text-center text-white">
                                        <div className="text-lg font-bold">
                                            Slot: {booking.startTime} - {booking.endTime}
                                        </div>
                                        <div className="text-sm font-medium">
                                            Date: {new Date(booking.date).toLocaleDateString("en-US")}
                                        </div>
                                    </div>

                                    {/* Body */}
                                    <div className="flex-1 flex flex-col px-6 py-6">
                                        <ul className="mb-6 space-y-3 text-gray-700 text-sm">
                                            <li>
                                                <span className="font-semibold">Name:</span> {booking.name}
                                            </li>
                                            <li>
                                                <span className="font-semibold">Phone:</span> {booking.phone}
                                            </li>
                                            <li>
                                                <span className="font-semibold">Email:</span> {booking.email}
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            )
                        ))
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex justify-center items-center mt-8 space-x-2">
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 disabled:opacity-50"
                        >
                            Prev
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => (
                            <button
                                key={i + 1}
                                onClick={() => handlePageChange(i + 1)}
                                className={`px-4 py-2 rounded-lg ${currentPage === i + 1
                                    ? "bg-blue-600 text-white"
                                    : "bg-gray-200 text-gray-700"
                                    }`}
                            >
                                {i + 1}
                            </button>
                        ))}
                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 disabled:opacity-50"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>
        </div>

    );
};

export default SalesrepDashboard;
